import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Sparkles, ShieldCheck } from "lucide-react";
import axios from "axios";
import logger from "../lib/logger";
import { DisclaimerContent } from "../components/DisclaimerContent";
import useSeo from "../hooks/useSeo";
import { PublicHeader } from "../components/PublicHeader";
import { AppMenu } from "../components/AppMenu";
import { SiteFooter } from "../components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Disclaimer screen.
 * - Gate mode (user has not yet accepted): shows checkbox + agree button.
 *   After accepting, user is redirected to /getting-started (first visit)
 *   or /dashboard based on activity.
 * - View-only mode (already accepted): shows text + back to dashboard.
 */
const DisclaimerPage = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  useSeo({
    title: "Disclaimer — Bilateral Calm",
    description:
      "Bilateral Calm's wellness disclaimer. Read about how the app supports (but doesn't replace) professional mental-health care.",
    path: "/disclaimer",
  });

  const requireAgreement = !user?.has_accepted_disclaimer;

  const handleAgree = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/disclaimer/accept`, {}, { withCredentials: true });
      // Refresh user so ProtectedRoute sees the updated flag
      await checkAuth();
      // Land on the dashboard directly — onboarding lives in-app now (the
      // 3D Ferris wheel + Trial banner serve as the welcome surface).
      navigate('/dashboard', { replace: true });
    } catch (error) {
      logger.error("Failed to record disclaimer acceptance:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="disclaimer-page">
      {/* Header — show the public nav for anonymous visitors so they can jump
          between Plans/About/FAQ/Sign in, but keep the signed-in header for
          authenticated users (logout, dashboard link, etc). */}
      {user ? (
        <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#3090A8] rounded-full flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-semibold text-xl text-[#1B2349]">Bilateral Calm</span>
            </div>
            {!requireAgreement && (
              <nav className="hidden md:flex items-center gap-6">
                <Link to="/dashboard" className="text-[#718096] hover:text-[#3090A8] transition-colors">Dashboard</Link>
                <Link to="/disclaimer" className="text-[#1B2349] font-medium hover:text-[#3090A8] transition-colors">Disclaimer</Link>
              </nav>
            )}
            <AppMenu />
          </div>
        </header>
      ) : (
        <PublicHeader />
      )}

      <main className="max-w-3xl mx-auto px-6 py-8 pb-24 app-prose">
        <div className="text-center mb-8 animate-fade-in-up">
          <div className="w-16 h-16 bg-[#EAF0F9] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-[#78C0D8]" />
          </div>
          <h1 className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349] mb-2">Important Disclaimer</h1>
          {requireAgreement ? (
            <p className="text-[#718096]">Please read and agree to the terms before using this app.</p>
          ) : (
            <p className="text-[#718096]">Please read before using this app.</p>
          )}
        </div>

        <div className="relative bg-white rounded-2xl p-6 sm:p-10 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 animate-fade-in-up overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#3090A8] via-[#78C0D8] to-[#E2B887] rounded-t-2xl" />

          <DisclaimerContent />

          {/* Agreement section — only shown when used as a gate */}
          {requireAgreement && (
            <div className="mt-8 pt-6 border-t border-slate-100">
              <label className="flex items-start gap-3 cursor-pointer mb-6" data-testid="agree-checkbox-label">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-slate-300 text-[#3090A8] focus:ring-[#3090A8]"
                  data-testid="agree-checkbox"
                />
                <span className="text-sm text-[#1B2349]">
                  I have read and understand the disclaimer above. I agree to the terms and accept full responsibility for my use of this app.
                </span>
              </label>
              <Button
                onClick={handleAgree}
                disabled={!agreed || submitting}
                className="w-full btn-primary text-lg py-4"
                data-testid="agree-btn"
              >
                {submitting ? "Processing..." : "I Agree"}
              </Button>
            </div>
          )}

          {/* View-only mode — just show a back button */}
          {!requireAgreement && (
            <div className="mt-8 text-center">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="rounded-full border-[#3090A8] text-[#3090A8] hover:bg-[#3090A8] hover:text-white"
                data-testid="back-to-dashboard-btn"
              >
                Back to Dashboard
              </Button>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default DisclaimerPage;
